// src/components/UploadDocumentModal.jsx
import React, { useState, useEffect, useRef } from "react";
import { Upload, X, CheckCircle, AlertCircle, Loader2, FileText, CloudUpload, Trash2 } from "lucide-react";
import {
  getUploadUrl,
  uploadFileToS3,
  saveDocumentMetadata,
} from "../api/documentApi";
import { toast } from "react-toastify";

export default function UploadDocumentModal({
  isOpen,
  onClose,
  staffId, // Keep for backward compatibility
  entityId,
  entityType = "Staff", // Default to Staff
  onUploadSuccessWithFiles,
  category
}) {
  const currentEntityId = entityId || staffId;
  const currentEntityType = entityType;
  const categories = ["Salary Documents", "Agreements & Contracts", "Audit Reports", "Timesheet", "Task Report","Risks","Reqisitions","Receipts","Recommendation Letters"];
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const generateId = () => Math.random().toString(36).slice(2, 9);

  // File validation
  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.txt'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (file.size > maxSize) {
      return `File "${file.name}" is too large. Maximum size is 10MB.`;
    }
    
    if (!allowedTypes.includes(fileExtension)) {
      return `File "${file.name}" has an unsupported format.`;
    }
    
    return null;
  };

  const handleFileChange = (event) => {
    console.log("File input change triggered");
    event.stopPropagation();
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
    event.target.value = '';
  };

  const addFiles = (selectedFiles) => {
    const validFiles = [];
    let errorMessages = [];

    selectedFiles.forEach((file) => {
      // Check for duplicates
      const isDuplicate = files.some(existingFile => 
        existingFile.file.name === file.name && existingFile.file.size === file.size
      );

      if (isDuplicate) {
        errorMessages.push(`"${file.name}" is already added.`);
        return;
      }

      const validationError = validateFile(file);
      if (validationError) {
        errorMessages.push(validationError);
        return;
      }

      validFiles.push({
        file,
        category: category || categories[0],
        id: generateId(),
        progress: 0,
        status: "pending",
      });
    });

    if (errorMessages.length > 0) {
      setError(errorMessages.join(' '));
      setTimeout(() => setError(null), 5000);
    } else {
      setError(null);
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    addFiles(droppedFiles);
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
    if (files.length <= 1) {
      setError(null);
    }
  };

  const updateFileCategory = (id, newCategory) => {
    setFiles(prev =>
      prev.map(f => f.id === id ? { ...f, category: newCategory } : f)
    );
  };

  const resetForm = () => {
    setFiles([]);
    setError(null);
    setDragActive(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadSingleFile = async (fileItem) => {
    try {
      const { uploadUrl, fileUrl, key } = await getUploadUrl({
        filename: fileItem.file.name,
        filetype: fileItem.file.type,
        entityId: currentEntityId,
        entityType: currentEntityType,
        category: fileItem.category,
      });

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl);
        xhr.setRequestHeader("Content-Type", fileItem.file.type);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            setFiles(prev =>
              prev.map(f =>
                f.id === fileItem.id
                  ? { ...f, progress: percent, status: "uploading" }
                  : f
              )
            );
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => {
          reject(new Error("Network error during upload"));
        };

        xhr.send(fileItem.file);
      });

      const newDoc = await saveDocumentMetadata({
        entityType: currentEntityType,
        entityId: currentEntityId.toString(),
        fileUrl,
        category: fileItem.category,
        originalName: fileItem.file.name,
        fileType: fileItem.file.name.split('.').pop().toLowerCase(),
        key,
      });

      setFiles(prev =>
        prev.map(f =>
          f.id === fileItem.id ? { ...f, progress: 100, status: "success", document: newDoc } : f
        )
      );

      return newDoc;
    } catch (e) {
      setFiles(prev =>
        prev.map(f => f.id === fileItem.id ? { ...f, status: "error" } : f)
      );
      throw e;
    }
  };

  const handleUpload = async () => {
    if (!files.length) {
      setError("Please select files to upload.");
      return;
    }
    
    if (!currentEntityId) {
      setError(`${currentEntityType} ID is required.`);
      return;
    }

    setIsUploading(true);
    setError(null); // Clear any existing errors at the start
    const uploadedDocuments = [];

    try {
      for (const fileItem of files) {
        if (fileItem.status !== "success") {
          const doc = await uploadSingleFile(fileItem);
          uploadedDocuments.push({ 
            _id: doc._id,
            fileUrl: doc.fileUrl, 
            originalName: doc.originalName,
            category: doc.category
          });
        } else if (fileItem.document) {
          uploadedDocuments.push({
            _id: fileItem.document._id,
            fileUrl: fileItem.document.fileUrl,
            originalName: fileItem.document.originalName,
            category: fileItem.document.category
          });
        }
      }

      // Success! Clear any errors and proceed
      setError(null);
      onUploadSuccessWithFiles(uploadedDocuments);
      toast.success(`${uploadedDocuments.length} document(s) uploaded successfully!`);
      
      // Small delay to show success message before closing
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);

    } catch (err) {
      console.error("Upload error:", err);
      setError("One or more files failed to upload. Please try again.");
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  // Get upload button state and text
  const getButtonState = () => {
    const successfulUploads = files.filter(f => f.status === 'success').length;
    const failedUploads = files.filter(f => f.status === 'error').length;
    const pendingUploads = files.filter(f => f.status === 'pending').length;
    const uploadingFiles = files.filter(f => f.status === 'uploading').length;

    if (isUploading) {
      return {
        text: uploadingFiles > 0 ? `Uploading ${uploadingFiles} files...` : "Processing...",
        disabled: true,
        showProgress: true
      };
    }

    if (files.length === 0) {
      return {
        text: "Select Files First",
        disabled: true,
        showProgress: false
      };
    }

    if (successfulUploads === files.length) {
      return {
        text: `✓ All ${files.length} files uploaded`,
        disabled: true,
        showProgress: false,
        success: true
      };
    }

    if (pendingUploads > 0) {
      return {
        text: `Upload ${pendingUploads} ${pendingUploads === 1 ? 'file' : 'files'}`,
        disabled: false,
        showProgress: false
      };
    }

    if (failedUploads > 0 && pendingUploads === 0) {
      return {
        text: `Retry ${failedUploads} failed ${failedUploads === 1 ? 'file' : 'files'}`,
        disabled: false,
        showProgress: false,
        isRetry: true
      };
    }

    return {
      text: "Upload Files",
      disabled: false,
      showProgress: false
    };
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const iconClass = "w-5 h-5";
    
    switch (ext) {
      case 'pdf': return <FileText className={`${iconClass} text-red-500`} />;
      case 'doc':
      case 'docx': return <FileText className={`${iconClass} text-blue-500`} />;
      case 'xls':
      case 'xlsx': return <FileText className={`${iconClass} text-green-500`} />;
      case 'png':
      case 'jpg':
      case 'jpeg': return <FileText className={`${iconClass} text-purple-500`} />;
      default: return <FileText className={`${iconClass} text-gray-500`} />;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Prevent scroll on body when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const buttonState = getButtonState();
  const successfulUploads = files.filter(f => f.status === 'success').length;
  const totalFiles = files.length;
  const hasErrors = files.some(f => f.status === 'error');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-amber-50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <CloudUpload className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Upload Documents</h2>
              <p className="text-sm text-gray-600">
                {totalFiles > 0 && successfulUploads > 0 && (
                  `${successfulUploads}/${totalFiles} files uploaded • `
                )}
                Add files to your submission
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isUploading}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Only show error message if there's an error AND not all files are successful */}
          {error && !(totalFiles > 0 && successfulUploads === totalFiles) && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div className="ml-3">
                  <p className="text-sm text-red-700 font-medium">Upload Error</p>
                  <p className="text-sm text-red-600 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Only show success message if all files are uploaded AND no errors */}
          {totalFiles > 0 && successfulUploads === totalFiles && !error && !hasErrors && (
            <div className="p-4 bg-green-50 border-l-4 border-green-400 rounded-lg">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <div className="ml-3">
                  <p className="text-sm text-green-700 font-medium">Upload Complete!</p>
                  <p className="text-sm text-green-600 mt-1">
                    All {totalFiles} files have been uploaded successfully.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Drag & Drop Upload Area */}
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
              dragActive
                ? 'border-orange-400 bg-orange-50 scale-[1.02]'
                : 'border-gray-300 hover:border-orange-400 hover:bg-gray-50'
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileChange}
              onClick={(e) => {
                e.stopPropagation();
                console.log("File input clicked");
              }}
              disabled={isUploading}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
            />
            
            <div className="space-y-4">
              <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
                dragActive ? 'bg-orange-100' : 'bg-gray-100'
              }`}>
                <CloudUpload className={`w-8 h-8 ${
                  dragActive ? 'text-orange-500' : 'text-gray-400'
                }`} />
              </div>
              
              <div>
                <h3 className={`text-lg font-semibold ${
                  dragActive ? 'text-orange-700' : 'text-gray-700'
                }`}>
                  {dragActive ? 'Drop files here' : 'Choose files or drag and drop'}
                </h3>
                <p className="text-sm text-gray-500 mt-2">
                  Supports: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, TXT
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Maximum file size: 10MB each
                </p>
              </div>
              
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log("Browse button clicked");
                  console.log("File input ref:", fileInputRef.current);
                  fileInputRef.current?.click();
                }}
                disabled={isUploading}
                className="inline-flex items-center px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="w-4 h-4 mr-2" />
                Browse Files
              </button>
            </div>
          </div>

          {/* Selected Files */}
          {files.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900">
                  Selected Files ({files.length})
                  {successfulUploads > 0 && (
                    <span className="ml-2 text-sm text-green-600 font-normal">
                      • {successfulUploads} uploaded
                    </span>
                  )}
                </h4>
                {!isUploading && (
                  <button
                    onClick={resetForm}
                    className="text-sm text-gray-500 hover:text-red-600 transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className="space-y-3 max-h-64 overflow-y-auto">
                {files.map(({ file, category, id, progress, status }) => (
                  <div key={id} className={`p-4 rounded-lg border transition-all ${
                    status === 'success' ? 'border-green-200 bg-green-50' :
                    status === 'error' ? 'border-red-200 bg-red-50' :
                    status === 'uploading' ? 'border-orange-200 bg-orange-50' :
                    'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                        {getFileIcon(file.name)}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{file.name}</p>
                          <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                          
                          <select
                            value={category}
                            onChange={(e) => updateFileCategory(id, e.target.value)}
                            disabled={isUploading || status === "uploading" || status === "success"}
                            className="mt-2 text-xs border border-gray-300 rounded px-2 py-1 bg-white disabled:bg-gray-100"
                          >
                            {categories.map((cat) => (
                              <option key={cat} value={cat}>
                                {cat}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-3">
                        {status === "pending" && (
                          <Upload className="text-gray-400 w-5 h-5" />
                        )}
                        {status === "uploading" && (
                          <div className="flex items-center space-x-2">
                            <Loader2 className="animate-spin text-orange-600 w-5 h-5" />
                            <span className="text-sm text-orange-600 font-medium">
                              {progress}%
                            </span>
                          </div>
                        )}
                        {status === "success" && (
                          <div className="flex items-center space-x-1">
                            <CheckCircle className="text-green-600 w-5 h-5" />
                            <span className="text-xs text-green-600 font-medium">Done</span>
                          </div>
                        )}
                        {status === "error" && (
                          <div className="flex items-center space-x-1">
                            <AlertCircle className="text-red-600 w-5 h-5" />
                            <span className="text-xs text-red-600 font-medium">Failed</span>
                          </div>
                        )}
                        
                        <button
                          onClick={() => removeFile(id)}
                          disabled={isUploading}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Progress bar for uploading files */}
                    {status === "uploading" && (
                      <div className="mt-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {files.length > 0 && (
              <span>
                {successfulUploads > 0 && successfulUploads < files.length && (
                  `${successfulUploads} of ${files.length} files uploaded`
                )}
                {successfulUploads === 0 && files.length > 0 && (
                  `${files.length} ${files.length === 1 ? 'file' : 'files'} ready`
                )}
                {successfulUploads === files.length && files.length > 0 && (
                  `All files uploaded successfully!`
                )}
              </span>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={isUploading}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {buttonState.success ? 'Done' : 'Cancel'}
            </button>
            
            <button
              onClick={handleUpload}
              disabled={buttonState.disabled || !currentEntityId}
              className={`px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 ${
                buttonState.success 
                  ? 'bg-green-600 text-white' 
                  : buttonState.isRetry
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-orange-600 hover:bg-orange-700 text-white'
              }`}
            >
              {isUploading ? (
                <>
                  <Loader2 className="animate-spin w-4 h-4" />
                  <span>{buttonState.text}</span>
                </>
              ) : buttonState.success ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>{buttonState.text}</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>{buttonState.text}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
